import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import RadioSwitchButton from "../formInputs/buttons/RadioSwitchButton";
import useStudentImages from "../../hooks/useStudentImages";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/GroupAccordionItem.scss";

/**
 * GroupAccordionItem component renders a single group within an accordion.
 * It displays group details, allows editing/deleting, and conditionally renders
 * the HomeroomGroupForm in a modal when editing.
 *
 * @component
 * @param {Object} dispatch
 * @param {string} eventKey
 * @param {Object} group
 * @param {Function} handleDeleteGroup
 * @param {Function} onEdit
 * @param {Object} selectedEpr
 */
const GroupAccordionItem = ({ dispatch, eventKey, grade, group, handleDeleteGroup, onEdit, selectedEpr }) => {
    const navigate = useNavigate();
    const { state: globalState } = useContext(GlobalContext);
    const { schoolYearDto, token } = globalState || {};

    const [viewMode, setViewMode] = useState("SUBJECTS");

    const handleShareGroupClick = () => {
        dispatch({ type: "SET_SELECTED_EPR", payload: selectedEpr });
        navigate(`/home/summary/share/${selectedEpr.key}/${group.key}`);
    };

    const handleViewModeChange = (e) => {
        setViewMode(e.target.value);
    };

    const { images: studentImages, loading } = useStudentImages(
        group.homeroomGroupStudents,
        schoolYearDto?.key,
        token,
        { fallbackImageUrl: "/img/avatar-placeholder.png" }
    );

    const studentTableRows = [...group.homeroomGroupStudents]
        .sort((a, b) => a.lastName.localeCompare(b.lastName))
        .map((student) => {
            const matchedStudent = selectedEpr.gradingProfileStudents.find(
                (s) => s.studentNumber === student.studentNumber
            );
            const hasComment = matchedStudent?.comment ? 1 : 0;
            const imageUrl = studentImages[student.studentNumber];

            return (
                <tr key={student.key}>
                    <td>
                        {" "}
                        {imageUrl && (
                            <img
                                src={imageUrl}
                                alt={`${student.firstName} ${student.lastName}`}
                                style={{ width: "45px", height: "60px", borderRadius: "5%", marginRight: "8px" }}
                            />
                        )}
                        {student.lastName}, {student.firstName}
                    </td>
                    <td>&ndash; of &ndash;</td>
                    <td>{hasComment}</td>
                    <td className="student-view-cell">
                        <div className="centered-button">
                            <ActionButton label="View" onClick={() => {}} />
                        </div>
                    </td>
                </tr>
            );
        });

    return (
        <Accordion.Item eventKey={eventKey} className="mb-3 group-accordion">
            <Accordion.Header>
                <span className="group-name-title">{group.name}</span>&ndash;{" "}
                <span className="group-student-count">{group.homeroomGroupStudents?.length} Students</span>
            </Accordion.Header>
            <Accordion.Body className="accordion-body-group">
                <div className="group-accordion-header-row">
                    <RadioSwitchButton
                        id={`view-mode-${group.key}`}
                        name="viewMode"
                        leftLabel="Subject Overview"
                        leftValue="SUBJECTS"
                        rightLabel="Student Overview"
                        rightValue="STUDENTS"
                        selectedValue={viewMode}
                        onChange={handleViewModeChange}
                    />
                    <div className="group-accordion-btn-group">
                        <ActionButton label="Share Group" onClick={handleShareGroupClick} />
                        <ActionButton className="action-button-reg" label="Edit Group" onClick={onEdit} />
                        <ActionButton
                            className="action-button-delete"
                            label="Delete Group"
                            onClick={() => handleDeleteGroup(group.key)}
                        />
                    </div>
                </div>
                <hr />
                {viewMode === "SUBJECTS" ? (
                    <>
                        <span className="group-name-title">Subjects</span>
                        <div className="epr-subjects-group">
                            {grade &&
                                grade.subjectToStandards.map((subject) => {
                                    return (
                                        <div key={subject.templateId} className="subject-card-group">
                                            <div className="subject-header-group">
                                                <span className="teacher-epr-subject-name-group">
                                                    {subject.subjectName}
                                                </span>
                                                <div className="subject-header-right-group">
                                                    <ActionButton
                                                        label="Input Ratings"
                                                        className="action-button-reg"
                                                        onClick={() => {}}
                                                    />
                                                    <ActionButton
                                                        label="View"
                                                        className="action-button-reg"
                                                        onClick={() => {}}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </>
                ) : (
                    <div className="epr-students-table">
                        <span className="group-name-title">Students</span>
                        <table>
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Content Status</th>
                                    <th>Comments</th>
                                    <th style={{ width: "1%", whiteSpace: "nowrap" }}></th>
                                </tr>
                            </thead>
                            <tbody>{studentTableRows}</tbody>
                        </table>
                        {loading && <LoadingSvg />}
                    </div>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
};

GroupAccordionItem.propTypes = {
    dispatch: PropTypes.func.isRequired,
    eventKey: PropTypes.string.isRequired,
    group: PropTypes.object.isRequired,
    grade: PropTypes.object,
    handleDeleteGroup: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    selectedEpr: PropTypes.object.isRequired
};

export default GroupAccordionItem;
